// components/Footer/Footer.js
import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Container,
  Grid,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  GitHub,
  Email,
} from "@mui/icons-material";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const footerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const socialLinks = [
    { icon: <Facebook />, name: "Facebook", url: "#" },
    { icon: <Twitter />, name: "Twitter", url: "#" },
    { icon: <Instagram />, name: "Instagram", url: "#" },
    { icon: <LinkedIn />, name: "LinkedIn", url: "#" },
    { icon: <GitHub />, name: "GitHub", url: "#" },
  ];

  const quickLinks = [
    { name: "About", url: "/about" },
    { name: "How It Works", url: "/how-it-works" },
    { name: "Contact", url: "mailto:services@findmyroommates.in" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", url: "/privacy" },
    { name: "Terms of Service", url: "/terms" },
    { name: "Cookie Policy", url: "/cookies" },
    { name: "Disclaimer", url: "/disclaimer" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background:
          "linear-gradient(180deg, rgba(0, 8, 20, 0.95) 0%, rgba(0, 29, 61, 1) 100%)",
        borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        position: "relative",
        overflow: "hidden",
        mt: "auto",
      }}
    >
      {/* Background Glow */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0,255,136,0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0,153,255,0.05) 0%, transparent 50%)
          `,
          zIndex: 1,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <motion.div
          variants={footerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Grid container spacing={4} sx={{ py: 6 }}>
            {/* Brand */}
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    background: "linear-gradient(45deg,#00ff88,#0099ff)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    mb: 2,
                  }}
                >
                  FindMyRoommates
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    mb: 3,
                    maxWidth: "300px",
                    lineHeight: 1.6,
                  }}
                >
                  Find the right roommates effortlessly — smart matching,
                  verified listings, and a community built for safe shared
                  living.
                </Typography>

                {/* Social */}
                <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                  {socialLinks.map((social) => (
                    <motion.div
                      key={social.name}
                      whileHover={{ scale: 1.2, y: -5 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <IconButton
                        aria-label={social.name}
                        href={social.url}
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          background: "rgba(255,255,255,0.1)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          backdropFilter: "blur(10px)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background: "rgba(255,255,255,0.2)",
                          },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>

                {/* Email */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Email sx={{ color: "#00ff88", fontSize: "1.1rem" }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    services@findmyroommates.in
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            {/* Explore */}
            <Grid item xs={6} sm={4} md={2}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 3,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      bottom: -8,
                      width: "30px",
                      height: "2px",
                      background: "linear-gradient(90deg,#00ff88,transparent)",
                    },
                  }}
                >
                  Explore
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {quickLinks.map((link) => (
                    <motion.div
                      key={link.name}
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <Link
                        href={link.url}
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          textDecoration: "none",
                          fontSize: "0.9rem",
                          transition: "0.3s",
                          "&:hover": { color: "#00ff88" },
                        }}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Legal */}
            <Grid item xs={6} sm={4} md={3}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 3,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      bottom: -8,
                      width: "30px",
                      height: "2px",
                      background: "linear-gradient(90deg,#0099ff,transparent)",
                    },
                  }}
                >
                  Legal
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {legalLinks.map((link) => (
                    <motion.div
                      key={link.name}
                      whileHover={{ x: 5 }}
                      transition={{ type: "spring", stiffness: 400 }}
                    >
                      <Link
                        href={link.url}
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          textDecoration: "none",
                          fontSize: "0.9rem",
                          transition: "0.3s",
                          "&:hover": { color: "#0099ff" },
                        }}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Bottom Bar */}
          <motion.div
            variants={itemVariants}
            style={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              padding: "24px 0",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.6)", textAlign: "center" }}
            >
              © {new Date().getFullYear()} FindMyRoommates. All rights
              reserved.
            </Typography>
          </motion.div>
        </motion.div>
      </Container>

      {/* Scroll to top */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          bottom: "20px",
          right: "20px",
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        onClick={() =>
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          })
        }
      >
        <motion.span
          animate={{ y: [0, -2, 0] }}
          transition={{ duration: 1, repeat: Infinity }}
          style={{ color: "#00ff88", fontSize: "1.5rem" }}
        >
          ↑
        </motion.span>
      </motion.div>
    </Box>
  );
};

export default Footer;
